import Chemistry from "@/components/molecules/Chemistry/Chemistry";
import PlayerCardCondensed, {
  CondensedCardPlayer,
  playerCardCondensedSize,
} from "@/design-system/PlayerCard/PlayerCardCondensed";
import { SourceChip } from "@/pages/Sbcs/components/SourceChip";
import { Player } from "@/types/SBCPlayer";
import {
  Chemistry as ChemistryLevel,
  StreamlineSource,
} from "@repo/shared/types";

/*
  The old pitch card was 140x190 and the layout is pegged to that: the slot
  positions and the pitch's aspect ratio both assume it. The condensed card's
  art is 190 wide, so this is the scale that lands on the same footprint.
*/
export const PITCH_CARD_SCALE = 140 / 190;

const LOCKED_CARD = `${import.meta.env.VITE_APP_ASSETS_BASE_URL}/fut23/cards/locked.png`;

/*
  A slot the solver could not fill comes back as a stand-in ("BRICK", resource
  id -1). It has no card art, price or chemistry, so it renders as the locked
  card rather than a broken one.
*/
export const isLockedPlayer = (player: Player) => player.resource_id === -1;

/*
  The card asset is not full-bleed: the card sits 10.3% down its own art and is
  82.9% of its height. The locked art has no such margin, so it is placed on the
  same box to land at the same size as the card next to it.
*/
const CARD_ART_TOP = 0.103;
const CARD_ART_HEIGHT = 0.829;

/** An unfilled slot: the locked card on the same footprint as a real one. */
export const LockedPlayerCard = ({
  scale = PITCH_CARD_SCALE,
}: {
  scale?: number;
}) => {
  const size = playerCardCondensedSize(scale);

  return (
    <div style={size} className="relative block shrink-0">
      <img
        src={LOCKED_CARD}
        alt=""
        loading="lazy"
        className="absolute left-1/2 -translate-x-1/2"
        style={{
          top: size.height * CARD_ART_TOP,
          height: size.height * CARD_ART_HEIGHT,
        }}
      />
    </div>
  );
};

/** The solver's player, as the shared condensed card wants it. */
export const toCardPlayer = (player: Player): CondensedCardPlayer => ({
  resourceId: player.resource_id,
  cardName: player.full_name ?? player.name,
  cardFont: player.cardFont,
  foil: player.foil,
  priceInfo: player.priceInfo,
});

/** A club player costs nothing to field; everyone else is bought. */
/* Which corner of the club a solver card sits in, for the chip under it. */
export const playerSource = (player: Player): StreamlineSource => {
  if (!player.user_owned) return "MARKET";
  if (player.sbc_storage) return "SBC-STORAGE";
  if (player.is_transfer_list) return "TRANSFER-LIST";
  if (player.duplicate) return "DUPLICATES";
  return "CLUB";
};

/**
 * One player on the pitch, built as a Streamline tier row builds one: the
 * shared condensed card with the source chip under it. The chip replaces the
 * position label, which only repeated what the slot already said. Chemistry is
 * the solver's own, so the Squad Builder's pips ride on top of the shared card.
 */
/* The swap arrows the old pitch drew, on the same dark disc as the chemistry
   badge: blue once swaps are in, the dimmed one turning while they load. */
const SWAP_ICON = `${import.meta.env.VITE_APP_ASSETS_BASE_URL}/fut23/misc/swap_icon_blue_on.png`;
const SWAP_ICON_LOADING = `${import.meta.env.VITE_APP_ASSETS_BASE_URL}/fut23/misc/swap_icon_blue_off.png`;

/**
 * "This card can be swapped", bottom right of a card, where the chemistry
 * badge would sit mirrored. Percentages rather than pixels so it holds its
 * place at every scale a card renders.
 */
export const SwapBadge = ({
  loading = false,
  size,
}: {
  loading?: boolean;
  size: number;
}) => (
  <div className="absolute bottom-[6%] right-[6%] rounded-full border border-panel-foreground bg-gray-800 p-[2px]">
    <img
      src={loading ? SWAP_ICON_LOADING : SWAP_ICON}
      alt=""
      className={loading ? "animate-swap-spin" : undefined}
      style={{ width: size, height: size }}
    />
  </div>
);

export const SolutionPlayerCard = ({
  player,
  scale = PITCH_CARD_SCALE,
  hasSwaps = false,
  swapsLoading = false,
}: {
  player: Player;
  scale?: number;
  /** Other cards could fill this slot: the swap badge shows, bottom right. */
  hasSwaps?: boolean;
  /** Swaps are being fetched: the badge shows dimmed and turning. */
  swapsLoading?: boolean;
}) => {
  const outOfPosition =
    player.chemistry === 0 && player.position_in_formation !== player.position;
  const chemistry = Math.min(
    3,
    Math.max(0, player.chemistry ?? 0),
  ) as ChemistryLevel;
  /* The chip is sized for the desktop card; a phone card is about two
     thirds of it, and it follows. The chemistry pips read best a step larger
     than the old card drew them. */
  const relative = scale / PITCH_CARD_SCALE;
  const small = relative < 0.8;
  const chemistryWeight = relative * 1.1;

  return (
    <div className="flex flex-col items-center">
      <div className="relative">
        <PlayerCardCondensed
          player={toCardPlayer(player)}
          scale={scale}
          priceSize={small ? "small" : "medium"}
          untradeable={player.untradeable}
        />
        {/* On the card, bottom left, where FUT puts it. Percentages rather than
            pixels so it holds its place at every scale the pitch renders. */}
        <div className="absolute bottom-[6%] left-[6%]">
          <Chemistry
            chemistry={chemistry}
            previousChemistry={chemistry}
            isOutOfPosition={outOfPosition}
            backgroundColor="bg-gray-800"
            weight={chemistryWeight}
          />
        </div>
        {/* The chemistry badge mirrored: same disc, same size, other corner. */}
        {(hasSwaps || swapsLoading) && (
          <SwapBadge loading={swapsLoading} size={chemistryWeight * 17} />
        )}
      </div>
      <span className="-mt-3">
        <SourceChip source={playerSource(player)} small={small} />
      </span>
    </div>
  );
};
